/**
 * Primitivas compartidas del plano de mesas.
 *
 * Todas las vistas del módulo `tables` (editor en Configuración, picker al
 * crear un pedido, dashboard `/tables`) comparten la misma geometría:
 * coordenadas virtuales 1000×600 que se escalan con `transform: scale()`
 * para quedar responsive sin scroll interno.
 *
 * Helpers aquí son puros: reciben capacidad de la mesa, devuelven tamaños.
 * El aspecto visual (colores, cursor, hover) lo resuelve cada consumidor.
 */

// ---------- Tipos ----------

export interface TablesTable {
  id: string;
  label: string;
  capacity: number;
  /** Centro de la mesa en coords virtuales (0..VIRTUAL.w, 0..VIRTUAL.h). */
  x: number;
  y: number;
  /** Mesa fuera de servicio: no se puede seleccionar al crear pedidos. */
  blocked?: boolean;
}

export interface TablesZone {
  id: string;
  name: string;
  tables: TablesTable[];
}

export interface TablesConfigShape {
  zones: TablesZone[];
}

// ---------- Constantes del canvas ----------

export const VIRTUAL = { w: 1000, h: 600 } as const;
export const ASPECT_RATIO = `${VIRTUAL.w} / ${VIRTUAL.h}`;
export const CHAIR_LONG = 22;
export const CHAIR_SHORT = 14;
export const CHAIR_GAP = 3;

// ---------- Geometría de la mesa ----------

/**
 * Cuántas sillas van en cada lado (top/bottom/left/right) según el cupo.
 * Capacidad 1 = 1 silla a la izquierda. 2 = 1 a cada lado. 3 = 1 arriba +
 * 1 a cada lado. 4+ = las excedentes se reparten arriba/abajo.
 */
export const chairCounts = (
  cap: number,
): { top: number; bottom: number; left: number; right: number } => {
  if (cap <= 0) return { top: 0, bottom: 0, left: 0, right: 0 };
  if (cap === 1) return { top: 0, bottom: 0, left: 1, right: 0 };
  if (cap === 2) return { top: 0, bottom: 0, left: 1, right: 1 };
  if (cap === 3) return { top: 1, bottom: 0, left: 1, right: 1 };
  const longTotal = cap - 2;
  const top = Math.ceil(longTotal / 2);
  const bottom = longTotal - top;
  return { top, bottom, left: 1, right: 1 };
};

/**
 * Tamaño del "plato" (la tabla misma) en coords virtuales. Círculo para
 * cupos 1-2, rectángulo que crece horizontalmente con capacidad 5+.
 */
export const plateSize = (
  cap: number,
): { w: number; h: number; round: boolean } => {
  if (cap <= 2) return { w: 58, h: 58, round: true };
  if (cap === 3) return { w: 66, h: 66, round: false };
  if (cap === 4) return { w: 78, h: 78, round: false };
  const { top, bottom } = chairCounts(cap);
  const longMax = Math.max(top, bottom);
  return { w: 70 + longMax * 30, h: 78, round: false };
};

/**
 * Bounding box completo (plato + sillas) — útil para centrar la mesa sobre
 * su coordenada `(x, y)` y para evitar colisiones al arrastrar en el editor.
 */
export const boundingSize = (cap: number): { w: number; h: number } => {
  const plate = plateSize(cap);
  const counts = chairCounts(cap);
  return {
    w:
      plate.w +
      (counts.left > 0 ? CHAIR_SHORT + CHAIR_GAP : 0) +
      (counts.right > 0 ? CHAIR_SHORT + CHAIR_GAP : 0),
    h:
      plate.h +
      (counts.top > 0 ? CHAIR_SHORT + CHAIR_GAP : 0) +
      (counts.bottom > 0 ? CHAIR_SHORT + CHAIR_GAP : 0),
  };
};

/**
 * Parser estricto de `tenant_features[tables].configJson`. Devuelve `null`
 * cuando no hay configuración o no hay zonas/mesas, para que las vistas
 * consumidoras puedan mostrar un estado vacío. El editor usa su propio
 * parser lenient que reemplaza `null` con una zona por defecto.
 */
export const parseConfig = (
  raw: string | undefined,
): TablesConfigShape | null => {
  if (!raw || raw === '{}') return null;
  try {
    const parsed = JSON.parse(raw) as Partial<TablesConfigShape>;
    if (
      !parsed.zones ||
      !Array.isArray(parsed.zones) ||
      parsed.zones.length === 0
    ) {
      return null;
    }
    return {
      zones: parsed.zones.map((z) => ({
        id: z.id ?? '',
        name: z.name ?? 'Zona',
        tables: Array.isArray(z.tables)
          ? (z.tables.filter(
              (t) =>
                typeof t.id === 'string' &&
                typeof t.label === 'string' &&
                typeof t.capacity === 'number' &&
                typeof t.x === 'number' &&
                typeof t.y === 'number',
            ) as TablesTable[])
          : [],
      })),
    };
  } catch {
    return null;
  }
};
